import React, { useMemo } from "react";
import { Breadcrumb as AntBreadcrumb, Typography } from "antd";
import { Link, useLocation, matchPath } from "react-router";
import { useLanguage } from "src/providers/LanguageProvider";

type Crumb = { label: React.ReactNode; path?: string };
type RouteCrumbConfig = {
  pattern: string;                       // '/products', '/products/:id' gibi
  crumbs: (params: Record<string, string>) => Crumb[]; // kırıntılar
  title?: (params: Record<string, string>) => React.ReactNode; // üst başlık (H3)
};

const Breadcrumb: React.FC<{ hideTitle?: boolean; homePath?: string }> = ({
  hideTitle,
  homePath = "/",
}) => {
  const { t } = useLanguage();
  const location = useLocation();
  console.log({ location });
  // Tüm sayfalar için ELLE tanım — burada ne yazarsan o görünür
  const routeMap: RouteCrumbConfig[] = useMemo(
    () => [
      {
        pattern: "/profile",
        crumbs: () => [{ label: t("MENU_PROFILE", "Profil") }],
        title: () => t("MENU_PROFILE", "Profil"),
      },
      {
        pattern: "/security",
        crumbs: () => [{ label: t("PROFILE_SECURITY", "Güvenlik") }],
        title: () => t("PROFILE_SECURITY", "Güvenlik"),
      },
      {
        pattern: "/security/sessions",
        crumbs: () => [{ label: t("MENU_SESSIONS", "Oturumlar") }],
        title: () => t("MENU_SESSIONS", "Oturumlar"),
      },
      {
        pattern: "/security/password",
        crumbs: () => [{ label: t("MENU_PASSWORD", "Şifre") }],
        title: () => t("MENU_PASSWORD", "Şifre"),
      },
      {
        pattern: "/setup-wizard",
        crumbs: () => [{ label: t("MENU_SUBSCRIPTIONS_WIZARD") }],
        title: () => t("MENU_SUBSCRIPTIONS_WIZARD"),
      },
      {
        pattern: "/subscriptions",
        crumbs: () => [{ label: t("MENU_SUBSCRIPTIONS", "Abonelikler") }],
        title: () => t("MENU_SUBSCRIPTIONS", "Abonelikler"),
      },
      {
        pattern: "/invoice-profiles",
        crumbs: () => [{ label: t("MENU_INVOICE_PROFILES", "Fatura profilleri") }],
        title: () => t("MENU_INVOICE_PROFILES", "Fatura profilleri"),
      },
      {
        pattern: "/my-invoices",
        crumbs: () => [{ label: t("MENU_MY_INVOICES", "Faturalarım") }],
        title: () => t("MENU_MY_INVOICES", "Faturalarım"),
      },
      // İsteğe bağlı: /my-invoices/:id için detay sayfası
      {
        pattern: "/my-invoices/:id",
        crumbs: (p) => [
          { label: t("MENU_MY_INVOICES", "Faturalarım"), path: "/my-invoices" },
          { label: t("MENU_INVOICE_DETAIL", "Fatura Detayı") },
        ],
        title: () => t("MENU_INVOICE_DETAIL", "Fatura Detayı"),
      },
      {
        pattern: "/products",
        crumbs: () => [{ label: t("MENU_PRODUCTS", "Ürünler") }],
        title: () => t("MENU_PRODUCTS", "Ürünler"),
      },
      // İsteğe bağlı: /products/:id için detay (adı dinamik almak istemiyorsan sabit yaz)
      {
        pattern: "/products/:id",
        crumbs: (p) => [
          { label: t("MENU_PRODUCTS", "Ürünler"), path: "/products" },
          { label: t("PRODUCT_DETAIL", "Ürün Detayı") },
        ],
        title: () => t("PRODUCT_DETAIL", "Ürün Detayı"),
      },
      {
        pattern: "/support",
        crumbs: () => [{ label: t("MENU_SUPPORT", "Destek") }],
        title: () => t("MENU_SUPPORT", "Destek"),
      },
    ],
    [t]
  );

  // En spesifik eşleşmeyi bul (ör. /products/:id, /products'tan önce gelsin)
  const matched = useMemo(() => {
    const bySpecificity = [...routeMap].sort(
      (a, b) => b.pattern.length - a.pattern.length
    );
    for (const cfg of bySpecificity) {
      const m = matchPath({ path: cfg.pattern, end: true }, location.pathname);
      if (m) return { cfg, params: (m.params as Record<string, string>) || {} };
    }
    return null;
  }, [routeMap, location.pathname]);

  // Home kırıntısı
  const homeCrumb: Crumb = { label: t("BREAD_HOME", "Ana sayfa"), path: homePath };

  // Breadcrumb items
  const items = useMemo(() => {
    const base = [{ title: <Link to={homeCrumb.path!}>{homeCrumb.label}</Link> }];
    if (!matched) return base; // eşleşmeyen path için sadece "Ana sayfa"
    const crumbs = matched.cfg.crumbs(matched.params);
    const antItems = crumbs.map((c, idx) => ({
      title: c.path ? <Link to={c.path}>{c.label}</Link> : <span>{c.label}</span>,
    }));
    return [...base, ...antItems];
  }, [matched, homeCrumb]);

  // Başlık (H3)
  const pageTitle = useMemo(() => {
    if (!matched) return "";
    if (matched.cfg.title) return matched.cfg.title(matched.params);
    const lastCrumb = matched.cfg.crumbs(matched.params).slice(-1)[0];
    return lastCrumb?.label ?? "";
  }, [matched]);

  return (
    <div className="page-header" style={{ marginBottom: 8 }}>
      <AntBreadcrumb items={items} />

    </div>
  );
};

export default Breadcrumb;
